import { useEffect, useRef, useState } from 'react';

import {
  Button,
  ButtonVariant,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextField,
} from '@cfa/react-components';

interface InputDialogProps {
  bodyText?: string;
  headerText: string;
  isDuplicatePlan?: boolean;
  isOpen: boolean;
  keyDownEnter?: boolean;
  onClose: () => void;
  secondaryButtonHandler?: () => void;
  secondaryButtonText: string;
  primaryButtonHandler?: (value: string) => void;
  primaryButtonText: string;
  primaryButtonVariant?: ButtonVariant;
  textInputDefaultValue?: string;
  textInputPlaceholder?: string;
}

const InputDialog: React.FC<InputDialogProps> = ({
  bodyText,
  headerText,
  isDuplicatePlan,
  isOpen,
  onClose,
  secondaryButtonHandler,
  secondaryButtonText,
  primaryButtonHandler,
  primaryButtonText,
  primaryButtonVariant,
  keyDownEnter,
  textInputDefaultValue,
  textInputPlaceholder,
}) => {
  const [textInputValue, setTextInputValue] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const onSecondaryClick = () => {
    setTextInputValue(textInputDefaultValue);
    secondaryButtonHandler?.();
  };

  return (
    <Modal onClose={onClose} show={isOpen} size="md">
      <>
        <ModalHeader className="text-[24px]!">{headerText}</ModalHeader>
        <ModalBody>
          <div
            dangerouslySetInnerHTML={{
              __html: bodyText as string | TrustedHTML,
            }}
          />
          <TextField
            ref={inputRef}
            data-testid="ModalInput"
            defaultValue={textInputDefaultValue}
            fullWidth={true}
            onChange={e => setTextInputValue(e.target.value)}
            onKeyDown={e => {
              if (keyDownEnter && e.key === 'Enter') {
                primaryButtonHandler?.(textInputValue ?? '');
              }
            }}
            placeholder={textInputPlaceholder}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            className="md:flex-1"
            color="secondary"
            data-testid="ConfirmationPopUpSecondaryButton"
            onClick={onSecondaryClick}
            variant="outlined"
          >
            {secondaryButtonText}
          </Button>
          <Button
            className="md:flex-1"
            color="secondary"
            data-testid="ConfirmationPopUpPrimaryButton"
            disabled={
              !isDuplicatePlan &&
              (!textInputValue || textInputValue === textInputDefaultValue)
            }
            onClick={() => {
              primaryButtonHandler?.(
                textInputValue ?? textInputDefaultValue ?? '',
              );
            }}
            variant={primaryButtonVariant}
          >
            {primaryButtonText}
          </Button>
        </ModalFooter>
      </>
    </Modal>
  );
};

export default InputDialog;
